import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import { adminApi } from '../../lib/adminApi';
import { Lock, Unlock, RotateCcw, Save, Rocket, PartyPopper, KeyRound, Eye, EyeOff } from 'lucide-react';

const SiteLockManager = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPw, setShowPw] = useState(false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminApi.get('/site-lock');
      setData(res.data);
    } catch (e) {
      toast({ title: 'Load failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!data) return;
    setSaving(true);
    try {
      await adminApi.post('/site-lock', {
        enabled: data.enabled,
        password: data.password,
        unlocks_remaining: data.unlocks_remaining,
        total_target: data.total_target,
        ribbon_theme: data.ribbon_theme,
        welcome_title: data.welcome_title,
        welcome_brand: data.welcome_brand,
        welcome_subtitle: data.welcome_subtitle,
      });
      toast({ title: 'Saved', description: 'Site lock settings updated.' });
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const resetCounter = async () => {
    if (!window.confirm('Re-arm the launch gate? The lock will turn ON and the counter will reset to the target.')) return;
    try {
      await adminApi.post('/site-lock/reset');
      toast({ title: 'Re-armed', description: 'Launch gate is active again.' });
      load();
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const clearVisitorSession = () => {
    try {
      sessionStorage.removeItem('ewrf.launch.session');
      localStorage.removeItem('ewrf.launch.unlocked');
      toast({ title: 'Cleared', description: 'Your browser will see the launch gate again on the public site.' });
    } catch (_) { /* ignore */ }
  };

  if (loading || !data) return <div className="p-8 text-center text-gray-500">Loading…</div>;

  const remainingPct = data.total_target ? Math.round(100 * (1 - (data.unlocks_remaining || 0) / data.total_target)) : 0;

  return (
    <div className="space-y-6" data-testid="site-lock-manager">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Rocket className="w-6 h-6 text-[#D4AF37]" />
            Site Launch Gate
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Password-lock the public site during your launch week. After the counter drains, the site goes fully live for everyone. Admin pages always bypass the gate.
          </p>
        </div>
      </div>

      {/* Status card */}
      <Card className={data.enabled ? 'border-amber-300' : 'border-green-300'}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {data.enabled ? <Lock className="w-5 h-5 text-amber-500" /> : <Unlock className="w-5 h-5 text-green-500" />}
            {data.enabled ? 'Gate is ACTIVE' : 'Gate is OFF — site is live for everyone'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Launch keys remaining</Label>
              <div className="text-4xl font-black text-[#0A1628]">
                {data.unlocks_remaining} <span className="text-lg font-normal text-gray-400">/ {data.total_target}</span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <Label className="text-xs uppercase tracking-widest text-gray-500 mr-2">Enable gate</Label>
              </div>
              <Switch
                checked={data.enabled}
                onCheckedChange={(v) => setData({ ...data, enabled: v })}
                data-testid="sitelock-enabled-toggle"
              />
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-gradient-to-r from-[#D4AF37] to-[#F59E0B] transition-all" style={{ width: `${remainingPct}%` }} />
          </div>
        </CardContent>
      </Card>

      {/* Settings */}
      <Card>
        <CardHeader><CardTitle>Settings</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="flex items-center gap-2"><KeyRound className="w-3.5 h-3.5" /> Launch password</Label>
            <div className="relative">
              <Input
                type={showPw ? 'text' : 'password'}
                value={data.password || ''}
                onChange={(e) => setData({ ...data, password: e.target.value })}
                className="pr-10 font-mono"
                data-testid="sitelock-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Visitors type this once per session to preview the site.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Keys remaining</Label>
              <Input
                type="number" min={0}
                value={data.unlocks_remaining ?? 0}
                onChange={(e) => setData({ ...data, unlocks_remaining: Number(e.target.value) || 0 })}
                data-testid="sitelock-remaining-input"
              />
              <p className="text-xs text-gray-500 mt-1">The site auto-releases when this hits 0.</p>
            </div>
            <div>
              <Label>Total target (rearm value)</Label>
              <Input
                type="number" min={1}
                value={data.total_target ?? 9}
                onChange={(e) => setData({ ...data, total_target: Number(e.target.value) || 1 })}
                data-testid="sitelock-total-input"
              />
              <p className="text-xs text-gray-500 mt-1">When you hit "Re-arm", the counter resets to this value.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ribbon theme */}
      <Card>
        <CardHeader>
          <CardTitle>Ribbon Colour Theme</CardTitle>
          <p className="text-xs text-gray-500 mt-1">Choose the colour of the ceremonial ribbon on the launcher screen.</p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { code: 'red',  label: 'Classic Red & Gold',   swatch: 'from-[#8B0000] via-[#DC143C] to-[#8B0000]', bg: 'from-[#1a0a0a] via-[#2b0e0e] to-[#4a0d0d]' },
              { code: 'blue', label: 'EWRF Blue & Gold',     swatch: 'from-[#0A1E52] via-[#1565C0] to-[#0A1E52]', bg: 'from-[#031024] via-[#062654] to-[#0D47A1]' },
            ].map((opt) => {
              const active = (data.ribbon_theme || 'red') === opt.code;
              return (
                <button
                  key={opt.code}
                  type="button"
                  onClick={() => setData({ ...data, ribbon_theme: opt.code })}
                  className={`relative overflow-hidden rounded-2xl border-2 p-4 text-left transition-all ${active ? 'border-[#D4AF37] shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid={`ribbon-theme-${opt.code}`}
                >
                  <div className={`absolute inset-0 bg-gradient-to-br ${opt.bg} opacity-10 pointer-events-none`} />
                  <div className="relative flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#F5E7A2] via-[#D4AF37] to-[#B8942F] flex items-center justify-center shadow">
                      <span className="text-white text-lg leading-none">✂</span>
                    </div>
                    <div className="flex-1">
                      <div className="font-bold text-sm text-[#0A1628]">{opt.label}</div>
                      <div className="text-[10px] uppercase tracking-widest text-gray-500">{active ? 'Currently active' : 'Click to select'}</div>
                    </div>
                    {active && <span className="text-[#D4AF37] text-lg">●</span>}
                  </div>
                  <div className={`mt-3 h-4 rounded-full bg-gradient-to-r ${opt.swatch} shadow-inner`} />
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Welcome text */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><PartyPopper className="w-5 h-5 text-[#D4AF37]" /> Welcome celebration text</CardTitle>
          <p className="text-xs text-gray-500">Shown to visitors after they unlock — after confetti + thunderclap.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Small lead-in line</Label>
            <Input
              value={data.welcome_title || ''}
              onChange={(e) => setData({ ...data, welcome_title: e.target.value })}
              placeholder="e.g. Welcome to the new"
              data-testid="sitelock-welcome-title"
            />
          </div>
          <div>
            <Label>Big brand headline</Label>
            <Input
              value={data.welcome_brand || ''}
              onChange={(e) => setData({ ...data, welcome_brand: e.target.value })}
              placeholder="e.g. EWRF.ORG.MY"
              data-testid="sitelock-welcome-brand"
            />
          </div>
          <div>
            <Label>Supporting subtitle</Label>
            <Textarea
              rows={3}
              value={data.welcome_subtitle || ''}
              onChange={(e) => setData({ ...data, welcome_subtitle: e.target.value })}
              placeholder="Two-line tagline shown under the brand"
              data-testid="sitelock-welcome-subtitle"
            />
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-4 z-10 flex flex-wrap items-center justify-end gap-3">
        <Button variant="outline" onClick={clearVisitorSession} data-testid="sitelock-clear-my-session">
          Clear my browser unlock (preview gate)
        </Button>
        <Button variant="outline" onClick={resetCounter} data-testid="sitelock-reset-btn">
          <RotateCcw className="w-4 h-4 mr-2" /> Re-arm gate
        </Button>
        <Button onClick={save} disabled={saving} className="bg-[#1565C0] shadow-lg" data-testid="save-sitelock-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </div>
  );
};

export default SiteLockManager;
